import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VDJ_MIRROR_CHUNK, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { normalizedPresets, coefficients, chaos, chaosJob, frame, COLLAPSE, RECOVER, TURN_BEATS } from './lib/flame-cpu.js';

// Flame - fractal flames, the luminous fractal veils of Electric Sheep.
// Hundreds of thousands of points draw an organic shape in silk of light,
// transforming continuously and reconfiguring on the drops. Psytrance,
// melodic techno, progressive, energetic electronic downtempo. The VDJ look
// applies (lib/vdjLook.js).
//
// THE CHAOS GAME. A flame is the attractor of 2 to 4 affine transforms each
// followed by a non-linear VARIATION. Every point is computed on its own: a
// seed (its texel, plus a salt that changes every frame), a start near the
// origin, ITER steps each picking a transform by hashing (seed, step),
// applying its affine then its variation. Its COLOUR is the running mean of
// the chosen transforms' colour indices (c = (c + ci) / 2): the point's
// history, which colours the branches. The game runs in a FRAGMENT pass, one
// texel per point, into an RGBA32F texture the points then draw from - on
// the Pi the V3D runs a vertex shader twice, and 400 000 points iterated in
// the vertex shader cost 441 ms there.
//
// THE FINENESS - temporal accumulation. Every point is ONE pixel, summed
// additively into a HalfFloat target at full resolution (tier 2) or 0.75
// (tiers 1, 0) - LinearFilter, never Nearest. The target is not cleared:
// accum = accum x DECAY + this frame's points, in a ping-pong pair, and the
// salt makes every frame add DIFFERENT points of the attractor: with DECAY
// 0.88 the screen holds about 8 frames' worth (200 000 a frame at tier 2,
// 1.6 million on screen). The shape moves slowly; the trail does not show.
//
// THE LOG TONEMAP, at display, on the accumulation:
//     density = accum.a
//     colour  = accum.rgb / density x log(1 + density k) / log(1 + k)
// then gamma 1/2.2 on that brightness and a vibrance (0.7 toward the
// saturated colour). k and the exposure are set by a governor that reads
// the density back every 15 frames: k puts the log's knee at the median
// density, the exposure shows the 98th percentile at 0.9. The 30% blur
// comes AFTER the log. ?flamelog=0 shows the linear sum: a white blot.
//
// THE FRAMING. Every beat (and at once on a preset change) a CPU chaos game
// of 8 000 points (lib/flame-cpu.js, the same maths) measures the 2-98%
// bounds with the current coefficients and global angle; the shape is
// scaled to 90% of the width (95% of the height at most) and centred,
// interpolated over a beat. When more than 25% of the points fall behind the
// cover, the shape is framed in the band left of it and mirrored: it opens
// around the cover in two wings. When the coverage of a 64 x 36 grid falls
// under 30%, the affines' rotation turns back until it is over 40% again.
// The presets were validated offline against that collapse
// (tools/validate-flames.mjs, every affine's |det| held in 0.25..0.85).

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'flame',
  family: 'particles',
  danceability: 0.82,
  requiresTier: 1,
  energyLevel: 0.74,
  bpmMin: 110,
  bpmMax: 150,
  movement: 0.70,
  tonality: 0.65,
  // the position cache and the accumulation are float render targets
  enabled: FLOAT_TEXTURES_OK
};

const DEFAULT_TIER = 2;
// blur: the post-log blur (at most 30%); off where its five tonemaps per
// pixel cost more than they give (the Pi's tiers)
const TIER = {
  2: { perFrame: 200000, res: 1.0, iter: 16, blur: 0.30 },
  1: { perFrame: 40000, res: 0.75, iter: 16, blur: 0 },
  0: { perFrame: 20000, res: 0.75, iter: 12, blur: 0 }
};
const POS_W = 1024;            // the position texture's width: one texel per point
const DECAY = 0.88;            // the accumulation: about 8 frames on screen
const PRESETS = normalizedPresets();

const FRAME_POINTS = 8000;     // the CPU chaos game of the framing
const FRAME_SLICES = 16;       // ... spread over this many frames (54 ms in one go on the Pi)
const DROP_FRAME_POINTS = 2000; // the drop's framing, in ONE frame
const GLOBAL_TURN_BEATS = 64;
const BAR_IMPULSE = 0.9;       // added angular speed (x base) on the bar, decaying
const BAR_IMPULSE_TAU = 0.6;   // seconds
const BEAT_GAIN = 1.4, BEAT_S = 0.10;
const PHRASE_FADE_BEATS = 1;
const DROP_HOT_BARS = 4;
const GAMMA = 2.2;
const VIBRANCE = 0.7;
const P98_LUM = 0.9;           // the governor: the 98th percentile of density shows at this
const GOVERN_EVERY = 15;
const DENS_W = 160, DENS_H = 90;
const SAFE_FLOOR = 0.45;
const MIRROR_CYCLE = [0, 1, 0, 3];   // none, horizontal, none, kaleido 6 - on the phrases
const SYNTH_BPM = 128;
const BEATS_PER_PHRASE = 16;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// =============================================================================
// SHADERS
// =============================================================================

function chaosFragment(iter) {
  return `
  precision highp float;
  uniform vec3 uAB[4];          // a, b, c
  uniform vec3 uDE[4];          // d, e, f
  uniform vec4 uCum;            // cumulative weights
  uniform vec4 uVarA;           // variation per transform
  uniform vec4 uVarB;           // the one it fades to (phrase)
  uniform vec4 uVarMix;
  uniform vec4 uColIdx;         // colour index per transform
  uniform vec2 uSalt;           // a new one every frame: different points each frame

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  vec2 variation(float v, vec2 p, float om) {
    float r2 = max(dot(p, p), 1e-6);
    float r = sqrt(r2);
    if (v < 0.5) return p;
    if (v < 1.5) return sin(p);
    if (v < 2.5) return p / r2;
    if (v < 3.5) { float s = sin(r2), c = cos(r2); return vec2(p.x * s - p.y * c, p.x * c + p.y * s); }
    if (v < 4.5) return vec2((p.x - p.y) * (p.x + p.y), 2.0 * p.x * p.y) / r;
    float th = atan(p.x, p.y) * 0.5 + om;
    return sqrt(r) * vec2(cos(th), sin(th));
  }

  void main() {
    // the texel IS the seed: column, row
    vec2 s = floor(gl_FragCoord.xy) + uSalt;
    // start near the origin, not in [-1, 1]^2: a slowly contracting preset
    // (feather, galaxy: 0.9 per step) still shows its starting square after
    // 16 steps, a hard-edged ghost; a small seed region leaves only a dot
    vec2 p = (vec2(hash12(s), hash12(s + 71.3)) * 2.0 - 1.0) * 0.05;
    float c = hash12(s + 19.7);
    for (int it = 0; it < ${iter}; it++) {
      vec2 hs = s + vec2(float(it) * 17.31 + 3.7, float(it) * 7.13 + 11.1);
      float u = hash12(hs);
      float om = hash12(hs + 5.5) < 0.5 ? 0.0 : 3.14159265;
      vec3 ab, de; float va, vb, vm, ci;
      if (u < uCum.x)      { ab = uAB[0]; de = uDE[0]; va = uVarA.x; vb = uVarB.x; vm = uVarMix.x; ci = uColIdx.x; }
      else if (u < uCum.y) { ab = uAB[1]; de = uDE[1]; va = uVarA.y; vb = uVarB.y; vm = uVarMix.y; ci = uColIdx.y; }
      else if (u < uCum.z) { ab = uAB[2]; de = uDE[2]; va = uVarA.z; vb = uVarB.z; vm = uVarMix.z; ci = uColIdx.z; }
      else                 { ab = uAB[3]; de = uDE[3]; va = uVarA.w; vb = uVarB.w; vm = uVarMix.w; ci = uColIdx.w; }
      vec2 q = vec2(ab.x * p.x + ab.y * p.y + ab.z, de.x * p.x + de.y * p.y + de.z);
      vec2 r = variation(va, q, om);
      if (vm > 0.001) r = mix(r, variation(vb, q, om), vm);
      p = clamp(r, vec2(-1e3), vec2(1e3));
      c = (c + ci) * 0.5;
    }
    float ok = (abs(p.x) < 50.0 && abs(p.y) < 50.0) ? 1.0 : 0.0;   // NaN fails both comparisons
    gl_FragColor = vec4(ok > 0.5 ? p : vec2(0.0), c, ok);
  }
`;
}

// The draw: a fetch, the global rotation, the framing and the camera kick.
// One pixel per point, always.
const pointsVertex = `
  precision highp float;
  // position.x = the point's index
  uniform highp sampler2D uPos;
  uniform vec2 uPosSize;
  uniform vec2 uFrameOffset;    // height units: the frame is 2 high, 2 x aspect wide
  uniform float uFrameScale;
  uniform float uAspect;
  uniform float uGlobalRot;
  uniform float uZoom;
  varying float vC;
  void main() {
    float i = position.x;
    vec2 uv = (vec2(mod(i, uPosSize.x), floor(i / uPosSize.x)) + 0.5) / uPosSize;
    vec4 t = texture2D(uPos, uv);
    vC = t.z;
    float cr = cos(uGlobalRot), sr = sin(uGlobalRot);
    vec2 w = (vec2(t.x * cr - t.y * sr, t.x * sr + t.y * cr) * uFrameScale + uFrameOffset) * uZoom;
    gl_Position = t.w > 0.5 ? vec4(w.x / uAspect, w.y, 0.0, 1.0) : vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 1.0;
  }
`;

const pointsFragment = `
  precision mediump float;
  uniform sampler2D uPalette;
  uniform float uWeight;
  varying float vC;
  void main() {
    vec3 col = texture2D(uPalette, vec2(clamp(vC, 0.0, 1.0) * (255.0 / 256.0) + 0.5 / 256.0, 0.5)).rgb;
    gl_FragColor = vec4(col * uWeight, uWeight);
  }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// accum x DECAY into the other target of the pair
const decayFragment = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform float uDecay;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(uPrev, vUv) * uDecay; }
`;

// THE TONEMAP (see the header). Linear-filtered HalfFloat; the blur is taken
// on the tonemapped colours, after the log, at POST_BLUR.
const displayFragment = `
  precision highp float;
  uniform sampler2D uAccum;
  uniform vec2 uTexel;
  uniform float uK;
  uniform float uExpo;
  uniform float uGain;
  uniform float uBlur;
  uniform float uLog;
  uniform float uDensityView;
  uniform float uMirror;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  ${VDJ_MIRROR_CHUNK}
  float bright(float d) { return pow(clamp(log(1.0 + d * uK) / log(1.0 + uK), 0.0, 8.0), 1.0 / ${GAMMA.toFixed(1)}) * uExpo; }
  vec3 tone(vec2 uv) {
    vec4 a = texture2D(uAccum, uv);
    float d = a.a;
    vec3 mean = a.rgb / max(d, 1e-4);
    vec3 col = mean * bright(d);
    float mx = max(col.r, max(col.g, col.b)), mn = min(col.r, min(col.g, col.b));
    vec3 sat = mx > 1e-4 ? (col - mn) / max(mx - mn, 1e-4) * mx : col;
    return mix(col, sat, ${VIBRANCE.toFixed(2)});
  }
  void main() {
    vec2 uv = vdjMirror(vUv, uMirror, uAspect);
    vec3 col;
    if (uDensityView > 0.5) {
      col = vec3(bright(texture2D(uAccum, uv).a));
    } else if (uLog > 0.5) {
      col = tone(uv);
      if (uBlur > 0.0) {
        vec3 nb = (tone(uv + vec2(uTexel.x, 0.0)) + tone(uv - vec2(uTexel.x, 0.0)) + tone(uv + vec2(0.0, uTexel.y)) + tone(uv - vec2(0.0, uTexel.y))) * 0.25;
        col = mix(col, nb, uBlur);
      }
    } else {
      col = texture2D(uAccum, uv).rgb * 0.5;     // the linear sum: it saturates
    }
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(sp - uSafeCenter) - uSafeHalfSize;
    float dd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, dd));
    gl_FragColor = vec4(col * uGain * att, 1.0);
  }
`;

// The governor's readback: the density at 160 x 90 cell centres, log-encoded
// in 8 bits (d = 2^(16 R) - 1).
const densityFragment = `
  precision highp float;
  uniform sampler2D uAccum;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(log2(1.0 + texture2D(uAccum, vUv).a) / 16.0, 0.0, 0.0, 1.0); }
`;

export class FlameVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const pr = parseInt(urlParam('flamepreset'), 10);
    this._forcedPreset = Number.isFinite(pr) && pr >= 0 && pr < PRESETS.length ? pr : null;
    this._log = urlParam('flamelog') !== '0';
    this._accumOn = urlParam('flameaccum') !== '0';
    this._densityView = urlParam('flamedensity') === '1';
    this._covDebug = urlParam('flamecov') === '1';
    const np = parseInt(urlParam('flamepoints'), 10);
    this._perFrame = Number.isFinite(np) && np > 1000 ? Math.min(np, 2000000) : this._layout.perFrame;
    this._post = new VdjPost({ bloomStrength: 0.55, vignette: 0.35 });
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    const n = this._perFrame;

    // --- the points: one vertex per texel of the position texture
    const pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) pos[i * 3] = i;
    this._pointsGeo = new THREE.BufferGeometry();
    this._pointsGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this._pointsGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
    this._palData = new Uint8Array(256 * 4);
    this._palTex = new THREE.DataTexture(this._palData, 256, 1, THREE.RGBAFormat);
    this._palTex.minFilter = this._palTex.magFilter = THREE.LinearFilter;
    this._palTex.colorSpace = THREE.NoColorSpace;

    // --- the chaos pass
    this._posH = Math.ceil(n / POS_W);
    this._posTarget = new THREE.WebGLRenderTarget(POS_W, this._posH, {
      type: THREE.FloatType, format: THREE.RGBAFormat, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._cu = {
      uAB: { value: [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()] },
      uDE: { value: [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()] },
      uCum: { value: new THREE.Vector4(1, 1, 1, 1) }, uVarA: { value: new THREE.Vector4() }, uVarB: { value: new THREE.Vector4() },
      uVarMix: { value: new THREE.Vector4() }, uColIdx: { value: new THREE.Vector4(0, 0.33, 0.66, 1) }, uSalt: { value: new THREE.Vector2() }
    };
    this._quadGeo = new THREE.PlaneGeometry(1, 1);
    this._chaosMat = new THREE.ShaderMaterial({ uniforms: this._cu, vertexShader: quadVertex, fragmentShader: chaosFragment(this._layout.iter), depthTest: false, depthWrite: false });
    this._chaosScene = this._quadScene(this._chaosMat);

    // --- the accumulation: decay pass + points, ping-pong
    this._decayU = { uPrev: { value: null }, uDecay: { value: DECAY } };
    this._decayMat = new THREE.ShaderMaterial({ uniforms: this._decayU, vertexShader: quadVertex, fragmentShader: decayFragment, depthTest: false, depthWrite: false, blending: THREE.NoBlending });
    this._decayScene = this._quadScene(this._decayMat);
    this._pu = {
      uPos: { value: this._posTarget.texture }, uPosSize: { value: new THREE.Vector2(POS_W, this._posH) },
      uFrameOffset: { value: new THREE.Vector2(0, 0) }, uFrameScale: { value: 0.5 },
      uAspect: { value: 16 / 9 }, uGlobalRot: { value: 0 }, uZoom: { value: 1 },
      uPalette: { value: this._palTex }, uWeight: { value: 1 }
    };
    this._pointsMat = new THREE.ShaderMaterial({
      uniforms: this._pu, vertexShader: pointsVertex, fragmentShader: pointsFragment,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneFactor,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._pointsMesh = new THREE.Points(this._pointsGeo, this._pointsMat);
    this._pointsMesh.frustumCulled = false;
    this._pointsScene = new THREE.Scene();
    this._pointsScene.add(this._pointsMesh);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._acc = null;             // [read, write]

    // --- the display
    this._du = {
      uAccum: { value: null }, uTexel: { value: new THREE.Vector2(1 / 1280, 1 / 720) },
      uK: { value: 1 }, uExpo: { value: 1 }, uGain: { value: 1 }, uBlur: { value: this._layout.blur }, uLog: { value: this._log ? 1 : 0 },
      uDensityView: { value: this._densityView ? 1 : 0 }, uMirror: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._displayMat = new THREE.ShaderMaterial({ uniforms: this._du, vertexShader: quadVertex, fragmentShader: displayFragment, depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(this._quadGeo, this._displayMat);
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._passes(renderer); };
    scene.add(this._display);

    // --- the governor's density readback
    this._densU = { uAccum: { value: null } };
    this._densMat = new THREE.ShaderMaterial({ uniforms: this._densU, vertexShader: quadVertex, fragmentShader: densityFragment, depthTest: false, depthWrite: false });
    this._densScene = this._quadScene(this._densMat);
    this._densTarget = new THREE.WebGLRenderTarget(DENS_W, DENS_H, { depthBuffer: false, stencilBuffer: false });
    this._densPixels = new Uint8Array(DENS_W * DENS_H * 4);
    this._comfort = new VdjComfort();
    this._frame = 0;
    this._k = 1; this._expo = 1; this._d50 = 0; this._d98 = 0; this._lum = 0;

    // --- the behaviour
    this._time = 0;
    this._beats = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._barCount = 0;
    this._beatCount = 0;
    this._beatAt = -1e9;
    this._angles = [0, 0, 0, 0];
    this._dir = 1;
    this._reversing = false;
    this._globalAngle = 0;
    this._impulse = 0;
    this._presetIndex = this._forcedPreset != null ? this._forcedPreset : 0;
    this._fade = null;
    this._mirrorTurn = 0;
    this._hotUntil = -1;
    this._lastDropKey = null;
    this._lastPaletteRef = undefined;
    this._vivid = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x7bff2f];
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._kick = { zoom: 1, roll: 0 };
    this._coeffs = [[], [], [], []];
    this._frameFrom = { s: 0.5, x: 0, y: 0 }; this._frameTo = { s: 0.5, x: 0, y: 0 }; this._frameT0 = -1e9;
    this._lastFraming = null;
    this._job = null;
    this._applyPreset(this._presetIndex);
    this._buildPalette(false);
    this._reframe(true);
    if (this._covDebug && typeof document !== 'undefined') {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;font:12px/1.5 ui-monospace,monospace;color:#9fe;background:rgba(0,0,0,.65);padding:6px 9px;border-radius:6px;white-space:pre';
      document.body.appendChild(el);
      this._covEl = el;
    }
  }

  _quadScene(mat) {
    const sc = new THREE.Scene();
    const m = new THREE.Mesh(this._quadGeo, mat); m.frustumCulled = false; sc.add(m);
    return sc;
  }

  _applyPreset(i) {
    const P = PRESETS[i];
    this._preset = P;
    const T = P.t;
    let w = 0; for (const t of T) w += t[0];
    let cum = 0;
    const c = [1, 1, 1, 1], va = [0, 0, 0, 0], ci = [0, 0, 0, 0];
    for (let k = 0; k < 4; k++) {
      const t = T[k];
      if (t) { cum += t[0] / w; c[k] = k === T.length - 1 ? 1.01 : cum; va[k] = t[7]; ci[k] = T.length > 1 ? k / (T.length - 1) : 0; }
    }
    this._cu.uCum.value.set(c[0], c[1], c[2], c[3]);
    this._cu.uVarA.value.set(va[0], va[1], va[2], va[3]);
    this._cu.uVarB.value.copy(this._cu.uVarA.value);
    this._cu.uVarMix.value.set(0, 0, 0, 0);
    this._cu.uColIdx.value.set(ci[0], ci[1], ci[2], ci[3]);
    this._varA = va.slice(); this._varB = va.slice(); this._varMix = [0, 0, 0, 0];
    this._fade = null;
  }

  // The palette: a 256-entry gradient through the cover's vivid colours,
  // forced to the VDJ saturation (1.0 after a drop: the hottest look).
  _buildPalette(hot) {
    const cols = this._vivid.map((h) => vdjColor(h, null, hot ? 1.0 : undefined));
    const n = cols.length, d = this._palData, c = new THREE.Color();
    for (let i = 0; i < 256; i++) {
      const x = i / 255 * n;
      const k = Math.floor(x) % n, f = x - Math.floor(x);
      c.copy(cols[k]).lerp(cols[(k + 1) % n], f);
      d[i * 4] = Math.round(c.r * 255); d[i * 4 + 1] = Math.round(c.g * 255); d[i * 4 + 2] = Math.round(c.b * 255); d[i * 4 + 3] = 255;
    }
    this._palTex.needsUpdate = true;
    this._paletteHot = !!hot;
  }

  // THE FRAMING: the CPU chaos game with the coefficients of the moment.
  // A framing run: started on the beat, advanced a slice per frame, applied
  // when done (interpolated over a beat). The drop and the first frame run
  // DROP_FRAME_POINTS at once and apply instantly.
  _startFraming() {
    if (this._job) return;
    coefficients(this._preset, this._angles, this._coeffs);
    this._job = chaosJob(this._preset, this._coeffs, FRAME_POINTS, 16, this._varA, this._varB, this._varMix);
    this._jobAngle = this._globalAngle;
  }
  _stepFraming() {
    if (!this._job) return;
    this._job.step(Math.ceil(FRAME_POINTS / FRAME_SLICES));
    if (this._job.done) { const pts = this._job.result(); this._job = null; this._applyFraming(pts, this._globalAngle, false); }
  }
  _reframe(instant) {
    this._job = null;
    coefficients(this._preset, this._angles, this._coeffs);
    const pts = chaos(this._preset, this._coeffs, DROP_FRAME_POINTS, 16, this._varA, this._varB, this._varMix);
    this._applyFraming(pts, this._globalAngle, instant);
  }
  _applyFraming(pts, globalAngle, instant) {
    const aspect = this._pu.uAspect.value;
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;   // R19: framed on the stage centre
    const f = frame(pts, globalAngle, aspect, { cx: sc.x, cy: sc.y, hw: sh.x, hh: sh.y });
    if (!f) return;
    this._lastFraming = f;
    // the collapse: turn back under COLLAPSE, until RECOVER
    if (!this._reversing && f.coverage < COLLAPSE) { this._reversing = true; this._dir = -this._dir; }
    else if (this._reversing && f.coverage > RECOVER) this._reversing = false;
    // the wings: a mirror to open around the cover
    if (f.wings && this._du.uMirror.value === 0) this._du.uMirror.value = 1;
    const cur = this._frameNow();
    this._frameFrom = cur;
    this._frameTo = { s: f.scale, x: f.ox, y: f.oy };
    this._frameT0 = instant ? -1e9 : this._time;
  }
  _frameNow() {
    const u = Math.min(1, (this._time - this._frameT0) / this._beatPeriod);
    const e = u * u * (3 - 2 * u), a = this._frameFrom, b = this._frameTo;
    return { s: a.s + (b.s - a.s) * e, x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e };
  }

  _ensureTargets(renderer) {
    renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, Math.round(_size.x * this._layout.res)), h = Math.max(2, Math.round(_size.y * this._layout.res));
    this._pu.uAspect.value = _size.x / _size.y;
    if (this._acc && this._acc[0].width === w && this._acc[0].height === h) return;
    if (this._acc) for (const t of this._acc) t.dispose();
    const mk = () => new THREE.WebGLRenderTarget(w, h, {
      type: THREE.HalfFloatType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._acc = [mk(), mk()];
    const prev = renderer.getRenderTarget();
    renderer.getClearColor(_clear); const pa = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 0);
    for (const t of this._acc) { renderer.setRenderTarget(t); renderer.clear(true, false, false); }
    renderer.setRenderTarget(prev); renderer.setClearColor(_clear, pa);
    this._du.uTexel.value.set(1 / w, 1 / h);
  }

  // Every frame: the chaos pass with a new salt, the decayed accumulation,
  // this frame's points on top; the governor every GOVERN_EVERY frames.
  _passes(renderer) {
    this._ensureTargets(renderer);
    if (this._frozen) { this._du.uAccum.value = this._acc[0].texture; return; }
    const prev = renderer.getRenderTarget(), prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    this._frame++;
    this._cu.uSalt.value.set((this._frame * 0.7548776662) % 1 * 977, (this._frame * 0.5698402909) % 1 * 977);
    renderer.setRenderTarget(this._posTarget);
    renderer.render(this._chaosScene, this._ortho);
    const read = this._acc[0], write = this._acc[1];
    this._decayU.uPrev.value = read.texture;
    this._decayU.uDecay.value = this._accumOn ? DECAY : 0;
    renderer.setRenderTarget(write);
    renderer.render(this._decayScene, this._ortho);
    renderer.render(this._pointsScene, this._ortho);
    this._acc = [write, read];
    this._du.uAccum.value = write.texture;
    if (this._frame % GOVERN_EVERY === 0) this._govern(renderer);
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
  }

  // THE GOVERNOR: the density at 160 x 90 points of the accumulation; k puts
  // the log's knee at the median of the lit cells, the exposure shows their
  // 98th percentile at P98_LUM. The same numbers feed the comfort rule.
  _govern(renderer) {
    this._densU.uAccum.value = this._acc[0].texture;
    renderer.setRenderTarget(this._densTarget);
    renderer.render(this._densScene, this._ortho);
    renderer.readRenderTargetPixels(this._densTarget, 0, 0, DENS_W, DENS_H, this._densPixels);
    const lit = [];
    for (let i = 0; i < this._densPixels.length; i += 4) {
      const r = this._densPixels[i];
      if (r > 0) lit.push(Math.pow(2, r / 255 * 16) - 1);
    }
    if (lit.length < 20) return;
    lit.sort((a, b) => a - b);
    const d50 = lit[Math.floor(lit.length * 0.5)], d98 = lit[Math.floor(lit.length * 0.98)];
    const k = 1 / Math.max(d50, 1e-3);
    const b98 = Math.pow(Math.log(1 + d98 * k) / Math.log(1 + k), 1 / GAMMA);
    const expo = P98_LUM / Math.max(b98, 1e-3);
    // smoothed: a governor, not a flicker
    this._k += (k - this._k) * 0.5;
    this._expo += (expo - this._expo) * 0.5;
    this._d50 = d50; this._d98 = d98;
    // the frame's mean brightness, for the 0.45 rule (colour taken at 0.6)
    let sum = 0;
    for (const d of lit) sum += Math.min(1.2, Math.pow(Math.log(1 + d * this._k) / Math.log(1 + this._k), 1 / GAMMA) * this._expo);
    this._lum = 0.6 * sum / (DENS_W * DENS_H);
    this._comfort.measure(this._lum, GOVERN_EVERY / 60);
  }

  _onBeat() {
    this._beatCount++;
    this._beatAt = this._time;
    this._startFraming();
  }
  _onBar() {
    this._barCount++;
    this._impulse = BAR_IMPULSE;
  }
  _onPhrase() {
    // one transform fades to another variation over a beat - the exception
    // to the cut: a flame cut mid-shape is unreadable
    const T = this._preset.t;
    const k = this._phraseTurn = ((this._phraseTurn || 0) + 1) % T.length;
    const from = this._varA[k];
    let to = (from + 1 + Math.floor(Math.random() * 5)) % 6;
    if (to === 1) to = 3;          // sinusoidal draws a square: kept only where a preset asked for it
    this._fade = { k, from, to, t0: this._time };
    this._varB[k] = to;
    this._cu.uVarB.value.setComponent(k, to);
    this._mirrorTurn = (this._mirrorTurn + 1) % MIRROR_CYCLE.length;
    this._du.uMirror.value = MIRROR_CYCLE[this._mirrorTurn];
  }
  _onDrop(director) {
    // RECONFIGURATION: a new preset at once, framed in the same frame, a
    // flash, the palette at its hottest
    if (this._forcedPreset == null) {
      let n = this._presetIndex;
      while (n === this._presetIndex) n = Math.floor(Math.random() * PRESETS.length);
      this._presetIndex = n;
    }
    this._applyPreset(this._presetIndex);
    this._phraseTurn = 0;
    this._reframe(true);
    if (this._comfort.requestFlash(this._time) && director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    this._hotUntil = this._time + DROP_HOT_BARS * 4 * this._beatPeriod;
    this._buildPalette(true);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._frozen = paused;
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    const kb = Math.min(1, dt / 0.15);
    this._bands.low += ((bands ? clamp01(bands.low) : 0.5) - this._bands.low) * kb;
    this._bands.mid += ((bands ? clamp01(bands.mid) : 0.5) - this._bands.mid) * kb;
    this._bands.high += ((bands ? clamp01(bands.high) : 0.5) - this._bands.high) * kb;
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (v && v.length) this._vivid = v.slice(0, 5);
      while (this._vivid.length < 2) this._vivid.push(0x2fd8ff);
      this._buildPalette(this._paletteHot);
    }

    if (!paused) {
      this._time += dt;
      const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
      let beatEdge = false;
      if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
      else if (Math.floor(this._beats + dt / this._beatPeriod) !== Math.floor(this._beats)) beatEdge = true;
      this._beats += dt / this._beatPeriod;
      const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
      if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
      else if (beatEdge && this._beatCount % 4 === 0) this._onBar();
      const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
      if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
      else if (beatEdge && this._beatCount % BEATS_PER_PHRASE === 0 && this._beatCount > 0) this._onPhrase();
      const ev = director && director.nextEvent;
      const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
      const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
      let dropped = false;
      if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
        const key = Math.round((nowMs + dropMs) / 100);
        if (key !== this._lastDropKey) { this._lastDropKey = key; this._onDrop(director); dropped = true; }
      }
      if (this._paletteHot && this._time > this._hotUntil) this._buildPalette(false);

      // THE MOTION: integrated angles - bands.mid sets the speed, the bar's
      // impulse adds to it and decays, the collapse guard sets the direction
      const speed = (0.7 + 0.6 * this._bands.mid) * (1 + this._impulse);
      this._impulse *= Math.exp(-dt / BAR_IMPULSE_TAU);
      const base = (Math.PI * 2) / (TURN_BEATS * this._beatPeriod);
      for (let k = 0; k < 4; k++) this._angles[k] += dt * base * speed * (k % 2 ? -1 : 1) * (1 + 0.15 * k) * this._dir;
      this._globalAngle += dt * (Math.PI * 2) / (GLOBAL_TURN_BEATS * this._beatPeriod) * (0.7 + 0.6 * this._bands.mid);
      if (this._fade) {
        const f = Math.min(1, (this._time - this._fade.t0) / (PHRASE_FADE_BEATS * this._beatPeriod));
        this._varMix[this._fade.k] = f;
        this._cu.uVarMix.value.setComponent(this._fade.k, f);
        if (f >= 1) {
          this._varA[this._fade.k] = this._fade.to; this._varMix[this._fade.k] = 0;
          this._cu.uVarA.value.setComponent(this._fade.k, this._fade.to);
          this._cu.uVarMix.value.setComponent(this._fade.k, 0);
          this._fade = null;
        }
      }
      if (beatEdge && !dropped) this._onBeat(); else if (beatEdge) this._beatCount++;
      this._stepFraming();
      const bp = barPhase != null ? barPhase : (this._beats % 4) / 4;
      this._kick = vdjCameraKick(bp, this._barCount);
      this._post.update(vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod));
    }

    // the coefficients of the moment, the same function the framing uses
    coefficients(this._preset, this._angles, this._coeffs);
    for (let k = 0; k < 4; k++) {
      const c = this._coeffs[k];
      this._cu.uAB.value[k].set(c[0], c[1], c[2]);
      this._cu.uDE.value[k].set(c[3], c[4], c[5]);
    }
    const fr = this._frameNow();
    this._pu.uFrameScale.value = fr.s;
    this._pu.uFrameOffset.value.set(fr.x, fr.y);
    this._pu.uGlobalRot.value = this._globalAngle + this._kick.roll;
    this._pu.uZoom.value = this._kick.zoom;

    // the draw: bands.high sets how many of this frame's points; the beat flares
    this._pointsGeo.setDrawRange(0, Math.round(this._perFrame * (0.7 + 0.3 * this._bands.high)));
    const beatHot = this._time - this._beatAt < BEAT_S ? BEAT_GAIN : 1;
    this._du.uK.value = this._k;
    this._du.uExpo.value = this._expo;
    this._du.uGain.value = (0.85 + 0.3 * this._bands.low) * beatHot * this._comfort.gain;
    if (this._covEl && this._frame % 10 === 0) {
      const f = this._lastFraming || {};
      this._covEl.textContent = 'flame  ' + this._preset.name + '  dir ' + this._dir + (this._reversing ? ' (reversing)' : '')
        + '\n       coverage ' + ((f.coverage || 0) * 100).toFixed(1) + '%  behind cover ' + ((f.inSafe || 0) * 100).toFixed(1) + '%' + (f.wings ? '  WINGS' : '')
        + '\n       bounds x ' + (f.bounds ? f.bounds[0].toFixed(2) + '..' + f.bounds[1].toFixed(2) + '  y ' + f.bounds[2].toFixed(2) + '..' + f.bounds[3].toFixed(2) : '-')
        + '\n       scale ' + fr.s.toFixed(3) + '  offset ' + fr.x.toFixed(2) + ',' + fr.y.toFixed(2)
        + '\n       density p50 ' + this._d50.toFixed(2) + ' p98 ' + this._d98.toFixed(2) + '  k ' + this._k.toFixed(2) + ' expo ' + this._expo.toFixed(2) + '  lum ' + this._lum.toFixed(3);
    }
  }

  getProbe() {
    const f = this._lastFraming || {};
    return {
      t: +this._time.toFixed(3), preset: this._preset.name, dir: this._dir, reversing: this._reversing,
      coverage: +(f.coverage || 0).toFixed(3), inSafe: +(f.inSafe || 0).toFixed(3), wings: !!f.wings, bounds: f.bounds ? f.bounds.map((v) => +v.toFixed(3)) : null,
      frame: this._frameNow(), mirror: this._du.uMirror.value, target: this._acc ? [this._acc[0].width, this._acc[0].height] : null,
      perFrame: this._perFrame, drawn: this._pointsGeo.drawRange.count, d50: +this._d50.toFixed(3), d98: +this._d98.toFixed(3),
      k: +this._k.toFixed(3), expo: +this._expo.toFixed(3), lum: +this._lum.toFixed(3), gain: +this._comfort.gain.toFixed(3), hot: this._paletteHot
    };
  }

  dispose() {
    this._scene.remove(this._display);
    this._pointsGeo.dispose(); this._pointsMat.dispose(); this._displayMat.dispose(); this._quadGeo.dispose();
    this._chaosMat.dispose(); this._decayMat.dispose(); this._densMat.dispose();
    this._palTex.dispose(); this._densTarget.dispose(); this._posTarget.dispose();
    if (this._acc) for (const t of this._acc) t.dispose();
    this._scene.background = this._prevBackground || null;
    if (this._covEl && this._covEl.parentNode) this._covEl.parentNode.removeChild(this._covEl);
  }
}

const _size = new THREE.Vector2();
const _clear = new THREE.Color();
